/**
 * WS-O / AUD-8 LB-2 (1): can this run actually write to the remembered folder — asked BEFORE
 * the first item, not discovered at the first refused write.
 *
 * `validated_path` (desktop `lib.rs`) requires the canonicalized target to sit under a
 * dialog-picked root; roots are re-canonicalized at boot and a root that no longer resolves is
 * silently dropped. On a USB stick that is the NORMAL case: unplug, relaunch, the root vanishes,
 * the remembered `saveDir` still shows in the UI, and every write comes back
 * `path is outside every folder you picked`. The old recovery was a modal folder dialog at the
 * END of an unattended seven-minute run — which nobody is watching, and which with write-through
 * would fire at item 1 and then a thousand times more.
 *
 * `"unknown"` is never treated as `"unwritable"`: a platform without the probe (web) and a probe
 * that itself failed must both fall through to today's behaviour, or a transient error becomes
 * an un-runnable app.
 */
import type { PlatformIO } from "@genz/platform";

export type SaveDirHealth = "writable" | "unauthorized" | "missing" | "unknown";

/**
 * The native refusal `validated_path` produces for a path outside every authorized root. Matched
 * on the SENTENCE because that is what crosses the bridge — `desktop.ts::writeBundleTo` already
 * matches the same phrase for its own `unauthorized` flag, so the two cannot drift apart.
 */
const UNAUTHORIZED_RE = /outside every folder you picked/i;
/** The OS's own "that path isn't there" — an unplugged drive, or a folder since deleted. */
const MISSING_RE = /no such file or directory|cannot find the path|ENOENT/i;

/**
 * Probe once, cheaply, through the EXISTING bridge. Never throws: every failure it cannot
 * classify is `"unknown"`, which blocks nothing.
 */
export async function probeSaveDir(
  io: Pick<PlatformIO, "ensureDir">,
  dir: string | undefined,
): Promise<SaveDirHealth> {
  if (!dir || !io.ensureDir) return "unknown"; // web, or no folder chosen yet
  try {
    await io.ensureDir(dir);
    return "writable";
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    if (UNAUTHORIZED_RE.test(message)) return "unauthorized";
    if (MISSING_RE.test(message)) return "missing";
    // A reason this build does not recognise is NOT a licence to refuse the run. Say nothing
    // and let the run proceed exactly as it did before this check existed.
    return "unknown";
  }
}

/**
 * The row state, in WS-B's status vocabulary: sentence case, no exclamation, `badge--warn` for
 * a recoverable state and `badge--danger` only for a genuine loss. A healthy folder is SILENT —
 * a badge that is always there is a badge nobody reads.
 */
export function saveDirHealthLabel(h: SaveDirHealth): { label: string; cls: string } | null {
  if (h === "unauthorized") return { label: "Folder needs re-picking", cls: "badge badge--warn" };
  if (h === "missing") return { label: "Folder not found", cls: "badge badge--warn" };
  return null; // "writable" and "unknown" say nothing and block nothing
}

/** The one sentence that names the cause AND the recovery. `null` where there is nothing to say. */
export function saveDirHealthNote(h: SaveDirHealth): string | null {
  if (h === "unauthorized") {
    return "This app's permission for that folder is gone — usually an external drive that was " +
      "unplugged, or a folder chosen in an older build. Pick it again and the run will write there.";
  }
  if (h === "missing") {
    return "That folder isn't on this machine right now. Reconnect the drive, or pick another folder.";
  }
  return null;
}

/** May a run start against a folder in this state? Only a NAMED bad state refuses. */
export function saveDirBlocksRun(h: SaveDirHealth): boolean {
  return h === "unauthorized" || h === "missing";
}
